import { ghlReadStatusSchema, ghlWriterStatusSchema, type GhlReadStatus, type GhlWriterStatus } from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { tabKey, type TabContext } from "../session/tab-context";

// La lettura da GoHighLevel (INGR-29, scelta di Claude 24/10/2026): lo stato della pagina «GoHighLevel», con la chiave dell'azienda
// della scheda (`["scheda", tabKey(tab), "imports", "ghl"]`), tolta con le altre al cambio di azienda. Le scritture sono del solo Super
// admin di Gioia365: salva e togli la chiave, prova la lettura; ognuna rimette lo stato che il server risponde. Il token va soltanto
// nel corpo di «Salva la chiave» e non torna mai.

// L'importazione (INGR-30) ha tre scritture in più: «Prova senza salvare», «Conferma i tag», «Importa adesso». Mentre una prova o
// un'importazione è in corso lo stato si rilegge ogni cinque secondi, finché il rapporto arriva.

export function ghlReadQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "imports", "ghl"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/imports/ghl", { schema: ghlReadStatusSchema, tab, signal }),
    refetchInterval: (query) => (query.state.data?.importer?.busy ? 5_000 : false),
  });
}

export function useGhlReadActions(tab: TabContext) {
  const queryClient = useQueryClient();
  const settle = (status: GhlReadStatus) => queryClient.setQueryData(ghlReadQuery(tab).queryKey, status);
  const saveKey = useMutation({
    mutationFn: (body: { token: string; locationId: string }) => apiRequest("PUT", "/api/imports/ghl/key", { schema: ghlReadStatusSchema, body, tab }),
    onSuccess: settle,
  });
  const removeKey = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/imports/ghl/key", { schema: ghlReadStatusSchema, tab }),
    onSuccess: settle,
  });
  const probe = useMutation({
    mutationFn: () => apiRequest("POST", "/api/imports/ghl/probe", { schema: ghlReadStatusSchema, tab }),
    onSuccess: settle,
  });
  const dryRun = useMutation({
    mutationFn: () => apiRequest("POST", "/api/imports/ghl/dry-run", { schema: ghlReadStatusSchema, tab }),
    onSuccess: settle,
  });
  const saveTags = useMutation({
    mutationFn: (body: { runId: string; entering: string[] }) => apiRequest("PUT", "/api/imports/ghl/tags", { schema: ghlReadStatusSchema, body, tab }),
    onSuccess: settle,
  });
  const importNow = useMutation({
    mutationFn: () => apiRequest("POST", "/api/imports/ghl/import", { schema: ghlReadStatusSchema, tab }),
    onSuccess: settle,
  });
  return { saveKey, removeKey, probe, dryRun, saveTags, importNow };
}

// La scrittura su GoHighLevel (INGR-19, 29/10/2026), soltanto per il Super admin di Gioia365: lo stato ha la sua chiave accanto a quella
// della lettura; l'interruttore e la «Prova senza scrivere» rimettono lo stato che il server risponde.

export function ghlWriteQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "imports", "ghl", "write"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/imports/ghl/write", { schema: ghlWriterStatusSchema, tab, signal }),
  });
}

export function useGhlWriteActions(tab: TabContext) {
  const queryClient = useQueryClient();
  const settle = (status: GhlWriterStatus) => queryClient.setQueryData(ghlWriteQuery(tab).queryKey, status);
  const save = useMutation({
    mutationFn: (body: { enabled: boolean; createMissing: boolean }) => apiRequest("PUT", "/api/imports/ghl/write", { schema: ghlWriterStatusSchema, body, tab }),
    onSuccess: settle,
  });
  const dryRun = useMutation({
    mutationFn: (body: { sample: number }) => apiRequest("POST", "/api/imports/ghl/write/dry-run", { schema: ghlWriterStatusSchema, body, tab }),
    onSuccess: settle,
  });
  return { save, dryRun };
}
